import React, { useState, useEffect } from "react";
import { useAuth } from "../../../hooks/useAuth";
import { useCoupons } from "../../../hooks/useCoupons";
import styles from "./AffiliateReferrals.module.css";

interface ReferralStats {
  total_referrals: number;
  credits_earned: number;
  recent_redemptions: any[];
}

interface ReferralData {
  referral_code: string;
  stats: ReferralStats;
}

const ReferralsTab: React.FC = () => {
  const { user } = useAuth();
  const { redeemCreditCoupon, isLoading: couponLoading } = useCoupons();
  const [referralData, setReferralData] = useState<ReferralData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copySuccess, setCopySuccess] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponMessage, setCouponMessage] = useState("");

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

  useEffect(() => {
    if (!user?.id) return;

    const fetchReferralData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/referrals/${user.id}`);
        if (response.ok) {
          const data = await response.json();
          setReferralData(data);
        } else {
          console.error("Failed to fetch referral data");
        }
      } catch (error) {
        console.error("Error fetching referral data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReferralData();
  }, [user?.id, API_URL]);

  const copyReferralCode = async () => {
    if (!referralData?.referral_code) return;

    try {
      await navigator.clipboard.writeText(referralData.referral_code);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy referral code:", error);
    }
  };

  const generateReferralLink = () => {
    if (!referralData?.referral_code) return "";
    return `${window.location.origin}/?coupon=${referralData.referral_code}`;
  };

  const copyReferralLink = async () => {
    const link = generateReferralLink();
    if (!link) return;

    try {
      await navigator.clipboard.writeText(link);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error("Failed to copy referral link:", error);
    }
  };

  const handleRedeemCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!couponCode.trim()) {
      setCouponMessage("Please enter a coupon code");
      return;
    }

    try {
      const result = await redeemCreditCoupon(couponCode.trim());
      if (result.success) {
        setCouponMessage(
          `Success! You received ${
            result.data?.granted_credits || "some"
          } credits.`
        );
        setCouponCode("");
        // Refresh referral data to update stats
        if (user?.id) {
          const response = await fetch(`${API_URL}/api/referrals/${user.id}`);
          if (response.ok) {
            const data = await response.json();
            setReferralData(data);
          }
        }
      } else {
        setCouponMessage(result.message || "Failed to redeem coupon");
      }
    } catch (error: any) {
      setCouponMessage(error.message || "Failed to redeem coupon");
    }
  };

  if (loading) {
    return (
      <div className={styles.referralsTab}>
        <div className={styles.loadingState}>
          <p>Loading your referral information...</p>
        </div>
      </div>
    );
  }

  if (!referralData) {
    return (
      <div className={styles.referralsTab}>
        <div className={styles.errorState}>
          <p>Failed to load referral information. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.referralsTab}>
      {/* Referral Code Section */}
      <div className={styles.referralCodeSection}>
        <div className={styles.referralCodeUserReferralCode}>
          <h4 className={styles.subsectionTitle}>Your Referral Code</h4>
          <div className={styles.referralCodeContainer}>
            <div className={styles.referralCodeDisplay}>
              <span className={styles.referralCode}>
                {referralData.referral_code}
              </span>
              <button
                onClick={copyReferralCode}
                className={styles.copyButton}
                title="Copy referral code"
              >
                {copySuccess ? <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FFFFFF" viewBox="0 0 256 256"><path d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z" opacity="0.2"></path><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FFFFFF" viewBox="0 0 256 256"><path d="M208,48V216a8,8,0,0,1-8,8H56a8,8,0,0,1-8-8V48a8,8,0,0,1,8-8H96a39.83,39.83,0,0,0-8,24v8h80V64a39.83,39.83,0,0,0-8-24h40A8,8,0,0,1,208,48Z" opacity="0.2"></path><path d="M168,152a8,8,0,0,1-8,8H96a8,8,0,0,1,0-16h64A8,8,0,0,1,168,152Zm-8-40H96a8,8,0,0,0,0,16h64a8,8,0,0,0,0-16Zm56-64V216a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V48A16,16,0,0,1,56,32H92.26a47.92,47.92,0,0,1,71.48,0H200A16,16,0,0,1,216,48ZM96,64h64a32,32,0,0,0-64,0ZM200,48H173.25A47.93,47.93,0,0,1,176,64v8a8,8,0,0,1-8,8H88a8,8,0,0,1-8-8V64a47.93,47.93,0,0,1,2.75-16H56V216H200Z"></path></svg>}
              </button>
            </div>
            <div className={styles.referralCodeNoteContainer}>
              <div className={styles.referralCodeNoteIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" 
                      width="20"
                      height="20"
                      fill="#205b51"
                      viewBox="0 0 256 256">
                  <path d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z" opacity="0.2"></path>
                  <path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path>
                </svg>
              </div>
              <p className={styles.referralCodeDescription}>
                Share this code with friends. They get <strong>5 credits</strong>{" "}
                when they use it, and you get <strong>10 credits</strong> as a
                bonus!
              </p>
              </div>
          </div>
        </div>
        
        {/* Referral Link Section */}
        <div className={styles.referralCodeUserReferralCode}>
          <h4 className={styles.subsectionTitle}>Referral Link</h4>
          <div className={styles.referralLinkContainer}>
            <div className={styles.referralLinkDisplay}>
              <input
                type="text"
                value={generateReferralLink()}
                readOnly
                className={styles.referralLinkInput}
              />
              <button
                onClick={copyReferralLink}
                className={styles.copyButton}
                title="Copy referral link"
              >
                {copySuccess ? <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FFFFFF" viewBox="0 0 256 256"><path d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z" opacity="0.2"></path><path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path></svg> : <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="#FFFFFF" viewBox="0 0 256 256"><path d="M208,48V216a8,8,0,0,1-8,8H56a8,8,0,0,1-8-8V48a8,8,0,0,1,8-8H96a39.83,39.83,0,0,0-8,24v8h80V64a39.83,39.83,0,0,0-8-24h40A8,8,0,0,1,208,48Z" opacity="0.2"></path><path d="M168,152a8,8,0,0,1-8,8H96a8,8,0,0,1,0-16h64A8,8,0,0,1,168,152Zm-8-40H96a8,8,0,0,0,0,16h64a8,8,0,0,0,0-16Zm56-64V216a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V48A16,16,0,0,1,56,32H92.26a47.92,47.92,0,0,1,71.48,0H200A16,16,0,0,1,216,48ZM96,64h64a32,32,0,0,0-64,0ZM200,48H173.25A47.93,47.93,0,0,1,176,64v8a8,8,0,0,1-8,8H88a8,8,0,0,1-8-8V64a47.93,47.93,0,0,1,2.75-16H56V216H200Z"></path></svg>}
              </button>
            </div>
            <div className={styles.referralCodeNoteContainer}>
            <div className={styles.referralCodeNoteIcon}>
              <svg xmlns="http://www.w3.org/2000/svg" 
                    width="20"
                    height="20"
                    fill="#205b51"
                    viewBox="0 0 256 256">
                <path d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z" opacity="0.2"></path>
                <path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path>
              </svg>
            </div>
            <p className={styles.referralLinkDescription}>
              Share this link and your referral code will be automatically
              applied!
            </p>
            </div>
          </div>
        </div>
      </div>

      {/* Coupon Redemption Section */}
      <div className={styles.referralRedeemSection}>
        <h4 className={styles.subsectionTitle}>Redeem Coupon Code</h4>
        <form
          onSubmit={handleRedeemCoupon}
          className={styles.couponRedemptionForm}
        >
          <div className={styles.couponInputContainer}>
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              placeholder="Enter coupon code"
              className={styles.couponInput}
              disabled={couponLoading}
            />
            <button
              type="submit"
              disabled={couponLoading || !couponCode.trim()}
              className={styles.redeemButton}
            >
              {couponLoading ? "Redeeming..." : "Redeem"}
            </button>
          </div>
          {couponMessage && (
            <p
              className={`${styles.couponMessage} ${
                couponMessage.includes("Success")
                  ? styles.successMessage
                  : styles.errorMessage
              }`}
            >
              {couponMessage}
            </p>
          )}
        </form>
        <div className={styles.couponDescriptionNotesContainer}>
            <div className={styles.couponDescriptionNote}>
              <div className={styles.couponDescriptionIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" 
                      width="20"
                      height="20"
                      fill="#205b51"
                      viewBox="0 0 256 256">
                  <path d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z" opacity="0.2"></path>
                  <path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path>
                </svg>
              </div>
                <p className={styles.couponDescription}>
                  Enter a coupon code to redeem credits or discounts.
                </p>               
            </div>
            <div className={styles.couponDescriptionNote}>
              <div className={styles.couponDescriptionIcon}>
                <svg xmlns="http://www.w3.org/2000/svg" 
                      width="20"
                      height="20"
                      fill="#205b51"
                      viewBox="0 0 256 256">
                  <path d="M224,128a96,96,0,1,1-96-96A96,96,0,0,1,224,128Z" opacity="0.2"></path>
                  <path d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"></path>
                </svg>
              </div>
                <p className={styles.couponDescription}>
                  Your own referral code cannot be used.
                </p>               
            </div>
        </div>
      </div>

      {/* Statistics Section */}
      <div className={styles.referralStatsSection}>
        <h4 className={styles.subsectionTitle}>Your Referral Statistics</h4>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>
              {referralData.stats.total_referrals}
            </div>
            <div className={styles.statLabel}>Total Referrals</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statNumber}>
              {referralData.stats.credits_earned}
            </div>
            <div className={styles.statLabel}>Credits Earned</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {referralData.stats.recent_redemptions.length > 0 && (
        <div className={styles.recentActivitySection}>
          <h4 className={styles.subsectionTitle}>Recent Activity</h4>
          <div className={styles.activityList}>
            {referralData.stats.recent_redemptions
              .filter(
                (redemption) => redemption.context?.role === "owner_bonus"
              )
              .slice(-3)
              .map((redemption, index) => (
                <div
                  key={redemption.id || index}
                  className={styles.activityItem}
                >
                  <div className={styles.activityIcon}>+</div>
                  <div className={styles.activityDetails}>
                    <div className={styles.activityText}>
                      You earned {redemption.granted_credits} credits from a
                      referral
                    </div>
                    <div className={styles.activityDate}>
                      {new Date(redemption.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Copy Success Message */}
      {copySuccess && (
        <div className={styles.copySuccessMessage}>Copied to clipboard! ✓</div>
      )}
    </div>
  );
};

export default ReferralsTab;

